(function (global) {
  'use strict';

  const MAX_CACHE_AGE_SECONDS = 7 * 24 * 60 * 60;
  const CONFIG_FILE = 'gb-seasonal-calendar.json';
  const evaluatedScript = global.document ? global.document.currentScript : null;

  function ageSeconds(headers) {
    if (!headers || typeof headers.get !== 'function') return 0;
    const raw = headers.get('age');
    if (raw == null || raw === '') return 0;
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > MAX_CACHE_AGE_SECONDS) return null;
    return parsed;
  }

  function serverNowFromHeaders(headers, roundTripMs) {
    if (!headers || typeof headers.get !== 'function') return null;
    const dateMs = Date.parse(headers.get('date') || '');
    const cacheAge = ageSeconds(headers);
    if (!Number.isFinite(dateMs) || cacheAge === null) return null;
    const safeRoundTrip = Number.isFinite(roundTripMs) ? Math.max(0, roundTripMs) : 0;
    // Shopify's edge sends a current Date header while Age reports object cache
    // residence. Adding Age to that Date advances the visitor clock twice.
    return dateMs + safeRoundTrip / 2;
  }

  function selectCampaign(events, nowMs, campaignOverride) {
    if (!Array.isArray(events) || !events.length || !Number.isFinite(nowMs)) return null;
    if (campaignOverride) return events.find(event => event.id === campaignOverride) || null;
    const ordered = [...events].sort((a, b) => a.start_ms - b.start_ms);
    const first = ordered[0];
    if (nowMs < first.start_ms && first.pre_horizon_fallback) return first;
    return ordered.find(event => nowMs >= event.start_ms && nowMs < event.end_ms) || null;
  }

  function validCalendar(config) {
    return !!(
      config &&
      config.schema_version === 2 &&
      Array.isArray(config.events) &&
      config.events.length > 0 &&
      config.events.every(event =>
        event && typeof event.id === 'string' &&
        Number.isFinite(event.start_ms) && Number.isFinite(event.end_ms) &&
        event.end_ms > event.start_ms &&
        typeof event.countdown_title === 'string' &&
        typeof event.end_label === 'string' &&
        (event.id === 'final_stock_sale' || (
          typeof event.desktop_url === 'string' &&
          typeof event.mobile_url === 'string' &&
          event.desktop_dimensions && Number.isFinite(event.desktop_dimensions.width) && Number.isFinite(event.desktop_dimensions.height) &&
          event.mobile_dimensions && Number.isFinite(event.mobile_dimensions.width) && Number.isFinite(event.mobile_dimensions.height) &&
          event.palette && typeof event.alt_text === 'string'
        ))
      )
    );
  }

  function configUrlFor(document, script) {
    const explicit = script && script.dataset && script.dataset.configUrl;
    if (explicit) return explicit;
    const secondary = document.querySelector('[data-gb-seasonal-config-url]');
    if (secondary && secondary.dataset && secondary.dataset.gbSeasonalConfigUrl) {
      return secondary.dataset.gbSeasonalConfigUrl;
    }
    if (script && script.src) {
      try { return new URL(CONFIG_FILE, script.src).href; } catch (ignore) {}
    }
    return '';
  }

  function nextLocalMidnightEpochMs(nowMs) {
    const nextMidnight = new Date(nowMs);
    nextMidnight.setHours(24, 0, 0, 0);
    return nextMidnight.getTime();
  }

  function countdownParts(endMs, nowMs) {
    const totalSeconds = Math.max(0, Math.floor((endMs - nowMs) / 1000));
    return {
      total_seconds: totalSeconds,
      hours: Math.floor(totalSeconds / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  }

  function timerOverrideApplies(productOverride, eventId) {
    return productOverride === true && eventId === 'final_stock_sale';
  }

  function twoDigits(value) {
    return String(value).padStart(2, '0');
  }

  function eventFromServerMarkup(root) {
    if (!root) return null;
    const endMs = Number(root.dataset.gbEndEpochMs);
    if (!Number.isFinite(endMs)) return null;
    return {
      id: root.dataset.gbEventId || '',
      end_ms: endMs,
      countdown_title: root.dataset.gbTitle || '',
      end_label: root.dataset.gbEndLabel || '',
      palette: null,
      desktop_url: null,
      mobile_url: null,
    };
  }

  function setText(root, selector, value) {
    const element = root && root.querySelector(selector);
    if (element && element.textContent !== value) element.textContent = value;
  }

  function cssSet(element, name, value) {
    if (!element || !element.style) return;
    if (value) element.style.setProperty(name, value);
    else element.style.removeProperty(name);
  }

  function applyPalette(root, palette) {
    if (!root) return;
    const values = palette || {};
    cssSet(root, '--gb-cx-background', values.background);
    cssSet(root, '--gb-cx-accent', values.accent);
    cssSet(root, '--gb-cx-title', values.title);
    cssSet(root, '--gb-cx-text', values.text);
    cssSet(root, '--gb-cx-box-background', values.box_background);
    cssSet(root, '--gb-cx-box-text', values.box_text);
    cssSet(root, '--gb-cx-box-label', values.box_label);
  }

  const INLINE_IMAGE_WIDTHS = [165, 360, 535, 750, 1070, 1500];

  function imageUrlWithWidth(url, width) {
    if (!url || typeof url !== 'string') return '';
    try {
      const parsed = new URL(url, 'https://getblaster.co');
      parsed.searchParams.set('width', String(width));
      return parsed.href;
    } catch (ignore) {
      return '';
    }
  }

  function responsiveImageSrcset(url) {
    if (!url) return '';
    return INLINE_IMAGE_WIDTHS
      .map(width => imageUrlWithWidth(url, width) + ' ' + width + 'w')
      .filter(value => !value.startsWith(' '))
      .join(', ');
  }

  function setImageAttribute(element, name, value) {
    if (!element) return;
    const lazyName = 'data-' + name;
    let changed = false;
    if (value) {
      if (element.getAttribute(name) !== value) {
        element.setAttribute(name, value);
        changed = true;
      }
    } else if (element.hasAttribute && element.hasAttribute(name)) {
      element.removeAttribute(name);
      changed = true;
    }
    if (element.hasAttribute && element.hasAttribute(lazyName)) {
      if (value) {
        if (element.getAttribute(lazyName) !== value) {
          element.setAttribute(lazyName, value);
          changed = true;
        }
      } else {
        element.removeAttribute(lazyName);
        changed = true;
      }
    }
    return changed;
  }

  function seasonalImageSurface(picture) {
    if (!picture || typeof picture.closest !== 'function') return null;
    return picture.closest('[data-gb-seasonal-section], [data-gb-seasonal-promo]');
  }

  function setSeasonalSurfaceVisible(surface, visible) {
    if (!surface) return;
    surface.hidden = !visible;
    if (visible) surface.removeAttribute('aria-hidden');
    else surface.setAttribute('aria-hidden', 'true');
  }

  function imageElements(picture) {
    const isImage = String(picture && picture.tagName || '').toLowerCase() === 'img';
    return {
      image: isImage ? picture : picture && picture.querySelector('[data-gb-seasonal-image], img'),
      source: isImage ? null : picture && picture.querySelector('[data-gb-seasonal-mobile-source]'),
      inline: !!(picture && picture.hasAttribute && picture.hasAttribute('data-gb-seasonal-inline-image')),
    };
  }

  function seasonalImageKey(image, source, eventId) {
    return [
      eventId || '',
      image && image.getAttribute('src') || '',
      image && image.getAttribute('srcset') || '',
      image && image.getAttribute('sizes') || '',
      source && source.getAttribute('srcset') || '',
    ].join('|');
  }

  function normalizedImageUrl(url) {
    if (!url) return '';
    try {
      const parsed = new URL(url, 'https://getblaster.co');
      parsed.searchParams.delete('width');
      return parsed.origin + parsed.pathname + '?' + [...parsed.searchParams.entries()]
        .sort(([leftKey, leftValue], [rightKey, rightValue]) => leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue))
        .map(([key, value]) => encodeURIComponent(key) + '=' + encodeURIComponent(value))
        .join('&');
    } catch (ignore) {
      return '';
    }
  }

  function loadedImageMatchesCurrentRequest(image, source) {
    if (!image || !image.currentSrc) return true;
    const current = normalizedImageUrl(image.currentSrc);
    if (!current) return false;
    const candidates = [image.getAttribute('src')];
    const srcset = image.getAttribute('srcset') || '';
    srcset.split(',').forEach(candidate => candidates.push(candidate.trim().split(/\s+/)[0]));
    const mobileSrcset = source && source.getAttribute('srcset') || '';
    mobileSrcset.split(',').forEach(candidate => candidates.push(candidate.trim().split(/\s+/)[0]));
    return candidates.some(candidate => normalizedImageUrl(candidate) === current);
  }

  function restoreSeasonalImageLoading(image) {
    if (!image || !image.dataset.gbSeasonalOriginalLoading) return;
    image.setAttribute('loading', image.dataset.gbSeasonalOriginalLoading);
    delete image.dataset.gbSeasonalOriginalLoading;
  }

  function bindSeasonalImageError(picture, image, source) {
    if (!image || !image.addEventListener || image.dataset.gbSeasonalErrorBound === 'true') return;
    image.dataset.gbSeasonalErrorBound = 'true';
    image.addEventListener('error', () => {
      image.dataset.gbSeasonalFailedKey = seasonalImageKey(image, source, picture.dataset.gbEventId);
      delete image.dataset.gbSeasonalPendingKey;
      setSeasonalSurfaceVisible(seasonalImageSurface(picture), false);
      restoreSeasonalImageLoading(image);
    });
    image.addEventListener('load', () => {
      const currentKey = seasonalImageKey(image, source, picture.dataset.gbEventId);
      const pendingKey = image.dataset.gbSeasonalPendingKey;
      const failedKey = image.dataset.gbSeasonalFailedKey;
      if ((pendingKey && pendingKey === currentKey || failedKey && failedKey === currentKey) && loadedImageMatchesCurrentRequest(image, source)) {
        delete image.dataset.gbSeasonalFailedKey;
        delete image.dataset.gbSeasonalPendingKey;
        setSeasonalSurfaceVisible(seasonalImageSurface(picture), true);
        restoreSeasonalImageLoading(image);
      }
    });
  }

  function restoreDefaultSeasonalImage(picture, image, source) {
    const defaultSrc = picture.getAttribute('data-gb-default-desktop-src');
    const defaultAlt = picture.getAttribute('data-gb-default-alt');
    if (!defaultSrc || !defaultAlt) return false;

    let changed = setImageAttribute(image, 'src', defaultSrc);
    if (picture.hasAttribute('data-gb-seasonal-inline-image') || image.hasAttribute('srcset')) {
      changed = setImageAttribute(image, 'srcset', picture.getAttribute('data-gb-default-desktop-srcset') || '') || changed;
    }
    changed = setImageAttribute(source, 'srcset', picture.getAttribute('data-gb-default-mobile-srcset') || '') || changed;
    if (image.getAttribute('alt') !== defaultAlt) image.setAttribute('alt', defaultAlt);
    const width = picture.getAttribute('data-gb-default-width');
    const height = picture.getAttribute('data-gb-default-height');
    if (width && image.getAttribute('width') !== width) image.setAttribute('width', width);
    if (height && image.getAttribute('height') !== height) image.setAttribute('height', height);
    return { ready: true, changed };
  }

  function applySeasonalImages(document, event) {
    const pictures = Array.from(document.querySelectorAll('[data-gb-seasonal-picture]'));
    const surfaces = Array.from(document.querySelectorAll('[data-gb-seasonal-promo], [data-gb-seasonal-section]'));
    if (!event) {
      surfaces.forEach(surface => setSeasonalSurfaceVisible(surface, false));
      return;
    }

    const handledSurfaces = new Set();
    pictures.forEach(picture => {
      const surface = seasonalImageSurface(picture);
      if (surface) handledSurfaces.add(surface);
      const { image, source, inline } = imageElements(picture);
      if (!image) {
        setSeasonalSurfaceVisible(surface, false);
        return;
      }
      bindSeasonalImageError(picture, image, source);

      const previousEventId = picture.dataset.gbEventId || '';
      let updated = false;
      let identityChanged = false;
      if (event.id === 'final_stock_sale') {
        const restored = restoreDefaultSeasonalImage(picture, image, source);
        updated = restored.ready;
        identityChanged = restored.changed;
      } else if (event.desktop_url && event.alt_text && event.desktop_dimensions) {
        if (source && !event.mobile_url) {
          updated = false;
        } else {
          const desktopSrc = inline ? imageUrlWithWidth(event.desktop_url, 1500) : event.desktop_url;
          identityChanged = setImageAttribute(image, 'src', desktopSrc);
          if (inline) identityChanged = setImageAttribute(image, 'srcset', responsiveImageSrcset(event.desktop_url)) || identityChanged;
          if (source) identityChanged = setImageAttribute(source, 'srcset', event.mobile_url) || identityChanged;
          if (image.getAttribute('alt') !== event.alt_text) image.setAttribute('alt', event.alt_text);
          if (image.getAttribute('width') !== String(event.desktop_dimensions.width)) image.setAttribute('width', String(event.desktop_dimensions.width));
          if (image.getAttribute('height') !== String(event.desktop_dimensions.height)) image.setAttribute('height', String(event.desktop_dimensions.height));
          updated = true;
        }
      }

      if (updated && previousEventId !== event.id) identityChanged = true;
      picture.dataset.gbEventId = updated ? event.id : 'image-unavailable';
      const failedKey = image.dataset.gbSeasonalFailedKey;
      const expectedKey = seasonalImageKey(image, source, event.id);
      if (updated && failedKey === expectedKey) updated = false;
      else if (failedKey) delete image.dataset.gbSeasonalFailedKey;
      if (updated && !identityChanged && image.complete === true && image.naturalWidth === 0) {
        image.dataset.gbSeasonalFailedKey = expectedKey;
        updated = false;
      }
      if (updated && identityChanged && image.complete === true && image.naturalWidth > 0 && loadedImageMatchesCurrentRequest(image, source)) {
        identityChanged = false;
      }
      if (updated && identityChanged) {
        image.dataset.gbSeasonalPendingKey = expectedKey;
        // A hidden pending lazy image cannot start loading. Fetch the swap
        // eagerly, then restore its original loading policy after load/error.
        if (image.getAttribute('loading') === 'lazy') {
          image.dataset.gbSeasonalOriginalLoading = 'lazy';
          image.setAttribute('loading', 'eager');
        }
      }
      const pendingKey = image.dataset.gbSeasonalPendingKey;
      const stillLoading = updated && pendingKey === expectedKey;
      picture.dataset.gbEventId = updated ? event.id : 'image-unavailable';
      setSeasonalSurfaceVisible(surface, updated && !stillLoading);
    });

    surfaces.forEach(surface => {
      if (!handledSurfaces.has(surface)) setSeasonalSurfaceVisible(surface, false);
    });
  }

  function saveOriginalStyle(element, property, key) {
    if (!element || element.dataset[key] === 'captured') return;
    element.dataset[key] = 'captured';
    element.dataset[key + 'Value'] = element.style[property] || '';
  }

  function restoreStyle(element, property, key) {
    if (!element || element.dataset[key] !== 'captured') return;
    element.style[property] = element.dataset[key + 'Value'] || '';
    delete element.dataset[key];
    delete element.dataset[key + 'Value'];
  }

  function restoreOffset(document, root, host) {
    const body = document.body;
    if (root) restoreStyle(body, 'marginTop', 'gbSeasonalOriginalMarginTop');
    if (host) {
      restoreStyle(host, 'margin', 'gbSeasonalOriginalMargin');
      restoreStyle(host, 'padding', 'gbSeasonalOriginalPadding');
      restoreStyle(host, 'lineHeight', 'gbSeasonalOriginalLineHeight');
      restoreStyle(host, 'display', 'gbSeasonalOriginalDisplay');
      host.hidden = true;
      host.style.display = 'none';
    }
    document.documentElement.style.removeProperty('--cx-banner-height');
    if (body.dataset.cxOffset === 'true') delete body.dataset.cxOffset;
  }

  function syncOffset(document, root, host) {
    if (!root || root.hidden || global.getComputedStyle(root).position !== 'fixed') return;
    const body = document.body;
    saveOriginalStyle(body, 'marginTop', 'gbSeasonalOriginalMarginTop');
    if (host) {
      saveOriginalStyle(host, 'margin', 'gbSeasonalOriginalMargin');
      saveOriginalStyle(host, 'padding', 'gbSeasonalOriginalPadding');
      saveOriginalStyle(host, 'lineHeight', 'gbSeasonalOriginalLineHeight');
      saveOriginalStyle(host, 'display', 'gbSeasonalOriginalDisplay');
      host.hidden = false;
      restoreStyle(host, 'display', 'gbSeasonalOriginalDisplay');
      host.style.margin = '0';
      host.style.padding = '0';
      host.style.lineHeight = '0';
    }
    const height = Math.ceil(root.getBoundingClientRect().height);
    if (height > 0) {
      document.documentElement.style.setProperty('--cx-banner-height', height + 'px');
      body.style.marginTop = height + 'px';
      body.dataset.cxOffset = 'true';
    }
  }

  function renderCountdown(root, event, nowMs, document, host) {
    if (!root) return;
    root.hidden = false;
    root.removeAttribute('aria-hidden');
    root.dataset.gbEventId = event.id;
    root.dataset.gbTitle = event.countdown_title || '';
    root.dataset.gbEndEpochMs = String(root.dataset.gbCampaignOverride ? nextLocalMidnightEpochMs(nowMs) : event.end_ms);
    root.dataset.gbEndLabel = root.dataset.gbCampaignOverride ? 'midnight in your local time' : (event.end_label || '');
    setText(root, '[data-gb-seasonal-copy]', event.countdown_title || '');
    applyPalette(root, event.palette);

    const productOverride = root.dataset.gbProductTimerOverride === 'true';
    const timerOverride = !root.dataset.gbCampaignOverride && timerOverrideApplies(productOverride, event.id);
    root.dataset.gbTimerOverride = timerOverride ? 'true' : 'false';

    const timer = root.querySelector('[data-gb-seasonal-clock]');
    if (timer) {
      const timerDescription = timerOverride
        ? 'Ends ' + (event.end_label || 'at the scheduled end time')
        : 'Ends at midnight in your local time';
      timer.setAttribute('aria-label', (event.countdown_title || 'Sale') + '. ' + timerDescription + '.');
    }

    if (!timerOverride) {
      const parts = countdownParts(nextLocalMidnightEpochMs(nowMs), nowMs);
      setText(root, '[data-h]', twoDigits(parts.hours));
      setText(root, '[data-m]', twoDigits(parts.minutes));
      setText(root, '[data-s]', twoDigits(parts.seconds));
    }
    root.dataset.gbSeasonalReady = 'true';
    syncOffset(document, root, host);
  }

  function hideSeasonalSurface(document, root, host) {
    if (root) {
      root.dataset.gbEventId = 'none';
      root.dataset.gbTimerOverride = 'false';
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
    }
    applySeasonalImages(document, null);
    restoreOffset(document, root, host);
  }

  async function fetchCalendarAndClock(fetchImpl, configUrl, performanceImpl) {
    const start = performanceImpl.now();
    const response = await fetchImpl(configUrl, { cache: 'no-store', credentials: 'omit' });
    const end = performanceImpl.now();
    if (!response || !response.ok) throw new Error('calendar config fetch failed');
    const config = await response.json();
    if (!validCalendar(config)) throw new Error('calendar config shape is invalid');
    return {
      config,
      server_ms: serverNowFromHeaders(response.headers, end - start),
      sampled_at_performance_ms: end,
    };
  }

  async function boot(document, windowObject) {
    if (windowObject.__gbSeasonalCalendarBooted) return;
    windowObject.__gbSeasonalCalendarBooted = true;

    const script = evaluatedScript ||
      document.querySelector('script[data-gb-seasonal-calendar-loader]') ||
      document.getElementById('gb-seasonal-calendar-loader');
    const configUrl = configUrlFor(document, script);
    const host = (script && script.closest('.shopify-section')) || null;
    if (!configUrl) {
      if (global.console && global.console.error) {
        global.console.error('[GetBlaster seasonal calendar] Config URL is missing; keeping the server-rendered campaign.');
      }
      return;
    }
    const loadPerf = windowObject.performance.now();
    const initialRoot = document.getElementById('countdown-banner');
    const fallbackMs = initialRoot ? Number(initialRoot.dataset.gbServerNowMs) : NaN;
    const currentRoot = () => document.getElementById('countdown-banner');
    let calendar = null;
    let clock = null;
    let refreshInFlight = null;
    let retryTimer = 0;
    let activeEvent = eventFromServerMarkup(initialRoot);
    let observedRoot = null;
    let resizeObserver = null;

    function nowMs() {
      if (clock) return clock.server_ms + Math.max(0, windowObject.performance.now() - clock.sampled_at_performance_ms);
      if (Number.isFinite(fallbackMs)) return fallbackMs + Math.max(0, windowObject.performance.now() - loadPerf);
      return Date.now();
    }

    function hostFor(root) {
      return (root && root.closest && root.closest('.shopify-section')) || host;
    }

    function draw() {
      const root = currentRoot();
      const currentHost = hostFor(root);
      if (!calendar) {
        if (activeEvent && root && (root.dataset.gbCampaignOverride || nowMs() < activeEvent.end_ms)) {
          renderCountdown(root, activeEvent, nowMs(), document, currentHost);
        } else {
          hideSeasonalSurface(document, root, currentHost);
        }
        return;
      }
      activeEvent = selectCampaign(calendar.events, nowMs(), root && root.dataset.gbCampaignOverride);
      if (!activeEvent) {
        hideSeasonalSurface(document, root, currentHost);
        return;
      }
      if (currentHost) currentHost.hidden = false;
      applySeasonalImages(document, activeEvent);
      renderCountdown(root, activeEvent, nowMs(), document, currentHost);
      if (windowObject.ResizeObserver && root && root !== observedRoot) {
        if (resizeObserver) resizeObserver.disconnect();
        resizeObserver = new windowObject.ResizeObserver(() => {
          const liveRoot = currentRoot();
          syncOffset(document, liveRoot, hostFor(liveRoot));
        });
        resizeObserver.observe(root);
        observedRoot = root;
      }
    }

    async function refresh() {
      if (refreshInFlight || !configUrl) return refreshInFlight;
      refreshInFlight = fetchCalendarAndClock(windowObject.fetch.bind(windowObject), configUrl, windowObject.performance)
        .then(result => {
          calendar = result.config;
          if (Number.isFinite(result.server_ms)) clock = result;
          if (retryTimer) windowObject.clearTimeout(retryTimer);
          retryTimer = 0;
          draw();
        })
        .catch(error => {
          if (global.console && global.console.error) {
            global.console.error('[GetBlaster seasonal calendar] Config fetch failed; using the server-rendered fallback.', error);
          }
          draw();
          if (!retryTimer) {
            retryTimer = windowObject.setTimeout(() => {
              retryTimer = 0;
              refresh();
            }, 30_000);
          }
        })
        .finally(() => { refreshInFlight = null; });
      return refreshInFlight;
    }

    draw();
    await refresh();

    if (initialRoot) windowObject.setInterval(draw, 1000);
    let resizeTimer = 0;
    windowObject.addEventListener('resize', () => {
      windowObject.clearTimeout(resizeTimer);
      resizeTimer = windowObject.setTimeout(() => {
        const liveRoot = currentRoot();
        syncOffset(document, liveRoot, hostFor(liveRoot));
      }, 50);
    }, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refresh();
    });
    document.addEventListener('shopify:section:load', () => draw());
    windowObject.addEventListener('pageshow', () => refresh());
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => {
      const liveRoot = currentRoot();
      syncOffset(document, liveRoot, hostFor(liveRoot));
    });
  }

  const helpers = {
    ageSeconds,
    serverNowFromHeaders,
    selectCampaign,
    countdownParts,
    nextLocalMidnightEpochMs,
    timerOverrideApplies,
    validCalendar,
    configUrlFor,
    twoDigits,
    fetchCalendarAndClock,
    imageUrlWithWidth,
    responsiveImageSrcset,
    applySeasonalImages,
    renderCountdown,
    boot,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (global.document) {
    const run = () => boot(global.document, global);
    if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', run, { once: true });
    else run();
  }
})(typeof window !== 'undefined' ? window : globalThis);
